import { Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { concat, of } from 'rxjs';
import { first, switchMap, tap } from 'rxjs/operators';
import { ClientService } from 'src/app/domain/services';
import { GoogleAnalyticsService } from 'src/app/shared/service/google-analytics.service';
import { ChangesNotSavedComponent } from '../../../shared/changes-not-saved';
import { PersonalInformationFormComponent } from '../../components';
import { applicationPresent } from '../../operators';
import { CurrentApplicationService } from '../../services';
import { ApplicationService } from './../../../application/services';
import { Application, SelectedProvider } from './../../models';

@Component({
    selector: 'app-personal-information',
    templateUrl: './personal-information.component.html',
    styleUrls: ['./../../application-page.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class PersonalInformationComponent
    extends ChangesNotSavedComponent
    implements OnInit
{
    /**
     * Programs, Providers and Sites selected on Provider page are stored in sessionStorage
     */
    private providerIdsFromProviderPage: Number[] | undefined;
    private programIdsFromProviderPage: Number[] | undefined;
    private providerSiteIdsFromProviderPage: Number[] | undefined;

    /**
     * List to populate the Providers flyout
     */
    selectedProviders: SelectedProvider[] | undefined;
    submitted = false; // because we are extending ChangesNotSavedComponent

    @ViewChild(PersonalInformationFormComponent) form:
        | PersonalInformationFormComponent
        | undefined;

    client$ = this._client.latest;

    fields$ = this.client$.pipe(
        switchMap((client) => this._client.getClientFields(1))
    );

    formValue$ = this._currentApplication.latest.pipe(
        applicationPresent(),
        first(),
        switchMap((application: Application) => {
            if (application?.applicationId) {
                return this.applicationService.getPersonalInfo(
                    application.applicationId!
                );
            } else return of(null);
        })
    );

    constructor(
        private readonly applicationService: ApplicationService,
        private readonly snackBar: MatSnackBar,
        private readonly router: Router,
        private readonly _transloco: TranslocoService,
        private readonly _currentApplication: CurrentApplicationService,
        private readonly _client: ClientService,
        private readonly _googleAnalyticsService: GoogleAnalyticsService
    ) {
        super();
    }

    ngOnInit() {
        this._googleAnalyticsService.pageView(
            'Application: Personal Information'
        );
        this.programIdsFromProviderPage =
            sessionStorage
                .getItem('applicationProgramIds')
                ?.split(',')
                .map(Number) ?? [];
        this.providerIdsFromProviderPage =
            sessionStorage
                .getItem('applicationProviderIds')
                ?.split(',')
                .map(Number) ?? [];
        this.providerSiteIdsFromProviderPage =
            sessionStorage
                .getItem('applicationProviderSiteIds')
                ?.split(',')
                .map(Number) ?? [];

        this._currentApplication.latest
            .pipe(
                applicationPresent(),
                first(),
                switchMap((application: Application) => {
                    if (application?.applicationId) {
                        const getProviderPrograms =
                            this.applicationService.getSelectedProviderPrograms(
                                application.applicationId!
                            );

                        const updateProviders = !application.isSubmitted
                            ? this.applicationService.patchCurrentApplication({
                                  applicationId: application.applicationId!,
                                  selectedProgramIds:
                                      this.programIdsFromProviderPage,
                                  selectedProviderIds:
                                      this.providerIdsFromProviderPage,
                                  selectedProviderSiteIds:
                                      this.providerSiteIdsFromProviderPage
                              })
                            : '';

                        // if providers were not selected on the Provider page, let's retrieve the previously saved providers,
                        // otherwise, persist the selected providers and return those
                        const selectedProviders =
                            this.providerIdsFromProviderPage === undefined ||
                            this.providerIdsFromProviderPage.length === 0
                                ? getProviderPrograms
                                : concat(updateProviders, getProviderPrograms);

                        return selectedProviders;
                    } else return of(null);
                })
            )
            .subscribe((res) => {
                if (res === null || res === undefined) {
                    this.providerIdsFromProviderPage = undefined;
                    this.programIdsFromProviderPage = undefined;
                    sessionStorage.removeItem('applicationProgramIds');
                    sessionStorage.removeItem('applicationProviderIds');
                    sessionStorage.removeItem('applicationProviderSiteIds');

                    this._currentApplication.load();
                } else this.selectedProviders = res;
            });
    }

    isChanged(): boolean {
        return this.form?.touched ?? false;
    }

    isSaved(): boolean {
        return this.submitted;
    }

    onSubmitted(personalInformation: any) {
        this.applicationService
            .savePersonalInfo(personalInformation)
            .pipe(
                switchMap(() =>
                    this._transloco
                        .selectTranslate('application.snack.saved')
                        .pipe(first())
                ),
                tap((message) => {
                    this.snackBar.open(message, undefined, {
                        duration: 4000
                    });
                    this.postSubmit();
                })
            )
            .subscribe();
    }

    private postSubmit() {
        this.submitted = true;
        this._currentApplication.load();
        this.router.navigateByUrl(
            `${runtimeConfig.clientCode}/application/background`
        );
    }
}
